"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Search,
  Users,
  Fuel,
  Snowflake,
  Car,
  AlertCircle,
  ShieldCheck,
  MapPin,
  ChevronDown,
  Heart,
  Settings2,
  X,
} from "lucide-react";
import { publicTransportService } from "@/services/transport/public-transport.service";
import type {
  Vehicle,
  VehicleSearchParams,
} from "@/types/transport/public-transport.types";

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(amount: number | null | undefined) {
  if (amount === null || amount === undefined) return "Price on request";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "LKR",
    maximumFractionDigits: 0,
  }).format(amount);
}

// The API only returns these two driver-option labels today — filtering is
// done client-side against vehicle.driverOption since there's no
// driverOptionUid lookup endpoint to search by uid.
type DriverFilter = "" | "Self Drive" | "Driver Included";

interface Filters {
  search: string;
  pickup: string;
  returnD: string;
  driverOption: DriverFilter;
  seats: string;
  ac: boolean;
  category: string;
}

const EMPTY_FILTERS: Filters = {
  search: "",
  pickup: "",
  returnD: "",
  driverOption: "",
  seats: "",
  ac: false,
  category: "",
};

// ─── Hero + Search Bar ────────────────────────────────────────────────────

function SearchBar({
  filters,
  onChange,
  onSubmit,
}: {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="relative overflow-hidden">
      {/* Background photo */}
      <div className="absolute inset-0">
        <Image
          src="/images/transport.webp"
          alt=""
          fill
          priority
          className="object-cover"
        />
        {/* <div className="absolute inset-0 bg-gradient-to-br from-[#1b0c3e]/95 via-[#3b1590]/90 to-[#6D28D9]/80" /> */}
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 pt-20 pb-32 sm:pt-24 sm:pb-40 text-center">
        <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 backdrop-blur-sm text-amber-300 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6">
          <ShieldCheck className="w-3.5 h-3.5" />
          Island-wide rentals, verified partners
        </span>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] mb-4 max-w-3xl mx-auto drop-shadow-sm">
          Your ride around Sri Lanka, sorted.
        </h1>

        <p className="text-purple-100 text-base sm:text-lg max-w-xl mx-auto">
          Self-drive or with a driver — book a car, van, SUV or scooter in
          minutes.
        </p>
      </div>

      {/* Search card — straddles the hero/content boundary */}
      <div className="relative max-w-5xl mx-auto px-4 -mt-20 sm:-mt-24 pb-8">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl shadow-purple-950/30 p-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 tracking-wide mb-1.5">
                Pickup
              </label>
              <input
                type="datetime-local"
                value={filters.pickup}
                onChange={(e) => onChange({ pickup: e.target.value })}
                className="w-full rounded-xl px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 tracking-wide mb-1.5">
                Return
              </label>
              <input
                type="datetime-local"
                value={filters.returnD}
                min={filters.pickup}
                onChange={(e) => onChange({ returnD: e.target.value })}
                className="w-full rounded-xl px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => onChange({ search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && onSubmit()}
                placeholder="Search by vehicle, make"
                className="w-full rounded-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>

            <div className="flex items-center bg-gray-50 dark:bg-gray-800 rounded-full p-1 shrink-0">
              {(
                [
                  ["", "Either"],
                  ["Self Drive", "Self-drive"],
                  ["Driver Included", "With driver"],
                ] as [DriverFilter, string][]
              ).map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => onChange({ driverOption: value })}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition whitespace-nowrap ${
                    filters.driverOption === value
                      ? "bg-purple-600 text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="relative shrink-0">
              <select
                value={filters.seats}
                onChange={(e) => onChange({ seats: e.target.value })}
                className="appearance-none rounded-full pl-4 pr-9 py-2.5 text-xs font-semibold border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-400 cursor-pointer"
              >
                <option value="">Any seats</option>
                <option value="2">2+ seats</option>
                <option value="4">4+ seats</option>
                <option value="6">6+ seats</option>
                <option value="8">8+ seats</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>

            <button
              type="button"
              onClick={() => onChange({ ac: !filters.ac })}
              aria-pressed={filters.ac}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold border transition ${
                filters.ac
                  ? "bg-purple-50 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300"
                  : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400"
              }`}
            >
              <Snowflake className="w-3.5 h-3.5" />
              A/C
            </button>

            <button
              type="button"
              onClick={onSubmit}
              className="shrink-0 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-2.5 rounded-full transition text-sm shadow-md shadow-amber-500/30"
            >
              <MapPin className="w-4 h-4" />
              Find a ride
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vehicle Card ───────────────────────────────────────────────────────────

function VehicleCard({
  vehicle,
  isFavorite,
  onToggleFavorite,
}: {
  vehicle: Vehicle;
  isFavorite: boolean;
  onToggleFavorite: (uid: string) => void;
}) {
  return (
    <Link
      href={`/transport/${vehicle.vehicleUid}`}
      className="group relative block bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 hover:border-purple-200 dark:hover:border-purple-800 transition-all duration-300"
    >
      <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-800 overflow-hidden">
        {vehicle.coverImageUrl ? (
          <img
            src={vehicle.coverImageUrl}
            alt={vehicle.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car className="w-12 h-12 text-purple-300 dark:text-purple-700" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        <span className="absolute top-3 left-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm text-[11px] font-semibold text-purple-700 dark:text-purple-300 px-2.5 py-1 rounded-full">
          {vehicle.vehicleCategory}
        </span>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorite(vehicle.vehicleUid);
          }}
          aria-pressed={isFavorite}
          aria-label="Save vehicle"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:scale-110 transition"
        >
          <Heart
            className={`w-4 h-4 ${
              isFavorite
                ? "fill-red-500 text-red-500"
                : "text-gray-500 dark:text-gray-300"
            }`}
          />
        </button>

        <span className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-[11px] font-semibold text-white px-2.5 py-1 rounded-full">
          {vehicle.driverOption}
        </span>
      </div>

      <div className="p-4">
        <h3 className="font-bold text-gray-900 dark:text-white text-base truncate">
          {vehicle.name}
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          {vehicle.vehicleMake} {vehicle.vehicleModel} · {vehicle.year}
        </p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400 mb-4">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {vehicle.seatingCapacity} seats
          </span>
          <span className="flex items-center gap-1">
            <Fuel className="w-3.5 h-3.5" /> {vehicle.fuelType}
          </span>
          <span className="flex items-center gap-1">
            <Settings2 className="w-3.5 h-3.5" /> {vehicle.transmissionType}
          </span>
          {vehicle.hasAirConditioning && (
            <span className="flex items-center gap-1">
              <Snowflake className="w-3.5 h-3.5" /> A/C
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
          <div>
            <p className="text-lg font-bold text-purple-600 dark:text-purple-400 leading-none">
              {formatCurrency(vehicle.pricePerDayLkr)}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">per day</p>
          </div>
          <span className="flex items-center gap-1.5 bg-gray-900 dark:bg-purple-600 group-hover:bg-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full transition">
            Book Now
          </span>
        </div>
      </div>
    </Link>
  );
}

function VehicleCardSkeleton() {
  return (
    <div className="rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200 dark:bg-gray-800" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4" />
        <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2" />
        <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-2/3" />
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded-full w-1/2 mt-3 ml-auto" />
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function TransportListPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const patchFilters = useCallback((patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
  }, []);

  const runSearch = useCallback((f: Filters) => {
    setLoading(true);
    setError(null);
    const params: VehicleSearchParams = {
      search: f.search || undefined,
      minimumSeatingCapacity: f.seats ? Number(f.seats) : undefined,
      hasAirConditioning: f.ac || undefined,
      pickupDateTime: f.pickup ? new Date(f.pickup).toISOString() : undefined,
      returnDateTime: f.returnD ? new Date(f.returnD).toISOString() : undefined,
      page: 1,
      pageSize: 50,
    };
    publicTransportService
      .searchVehicles(params)
      .then((res) => setVehicles(res.items))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    runSearch(EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => runSearch(filters);

  const toggleFavorite = useCallback((uid: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }, []);

  // Category filter is client-side — there's no category lookup endpoint yet
  // (see comment in public-transport.types.ts), so chips are built from
  // whatever categories the current result set actually contains.
  const categories = useMemo(() => {
    const set = new Set(vehicles.map((v) => v.vehicleCategory).filter(Boolean));
    return Array.from(set);
  }, [vehicles]);

  const displayedVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      if (filters.driverOption && v.driverOption !== filters.driverOption)
        return false;
      if (filters.category && v.vehicleCategory !== filters.category)
        return false;
      return true;
    });
  }, [vehicles, filters.driverOption, filters.category]);

  const hasActiveFilters =
    !!filters.search ||
    !!filters.driverOption ||
    !!filters.seats ||
    filters.ac ||
    !!filters.category ||
    !!filters.pickup ||
    !!filters.returnD;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    runSearch(EMPTY_FILTERS);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SearchBar
        filters={filters}
        onChange={patchFilters}
        onSubmit={handleSubmit}
      />

      <div className="max-w-6xl mx-auto px-4 pb-16">
        {categories.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-4 -mt-2">
            <button
              type="button"
              onClick={() => patchFilters({ category: "" })}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                filters.category === ""
                  ? "bg-purple-600 border-purple-600 text-white"
                  : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
              }`}
            >
              All types
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => patchFilters({ category: c })}
                className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                  filters.category === c
                    ? "bg-purple-600 border-purple-600 text-white"
                    : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {loading
              ? "Searching…"
              : `${displayedVehicles.length} vehicle${
                  displayedVehicles.length === 1 ? "" : "s"
                } available`}
          </h2>
          {hasActiveFilters && !loading && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline"
            >
              <X className="w-3.5 h-3.5" />
              Clear filters
            </button>
          )}
        </div>

        {error && (
          <div className="text-center py-16">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        )}

        {!error && loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <VehicleCardSkeleton key={i} />
            ))}
          </div>
        )}

        {!error && !loading && displayedVehicles.length === 0 && (
          <div className="text-center py-16">
            <Car className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
              No vehicles match these filters.
            </p>
            <p className="text-xs text-gray-400">
              Try widening your search or clearing a filter.
            </p>
          </div>
        )}

        {!error && !loading && displayedVehicles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {displayedVehicles.map((v) => (
              <VehicleCard
                key={v.vehicleUid}
                vehicle={v}
                isFavorite={favorites.has(v.vehicleUid)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
